import * as Wj from '@grapecity/wijmo.react.grid';
import { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../../common/context/';

import {
    dateTimeFormat,
    formatDate,
    formatShowDate,
    getChildNodes,
    getDatesBetweenDates,
    getDayOfWeek,
    getFirstInterruption,
    getHolidayDay,
    getInterruptionListByDate,
    getMonth,
    getWorkshiftScheduling,
    showDateInfo,
} from '../../../utils/';

import { TableProps } from './TableProps';

import { NodeModel } from '@minoru/react-dnd-treeview';
import {
    ExceededWeeklyScheduleModel,
    InterruptionModel,
    WorkshiftSchedulingModel,
} from '../../../models';
import { InconsistenciesByEmployeeModel } from '../../../models/inconsistencies-validation/inconsistenciesValidation';

/**
 * Tiene toda la lógica para renderizar la tabla de la programacion y sus demas elementos
 * @param TableProps
 * @returns Componente que renderiza toda la tabla de las programaciones
 */
export const Table = ({
    endDate,
    startDate,
    treeEmployees,
    treeEmployeesCopy,
    groupsList,
    grid,
    setGrid,
}: TableProps) => {
    const [employeesToShow, setEmployeesToShow] = useState<NodeModel[]>([]);
    const { userInfo, parametersApplication } = useContext(AppContext);
    const [isUpdating, setIsUpdating] = useState(false);
    const [savedSelection, setSavedSelection] = useState<{
        row: number;
        col: number;
        row2: number;
        col2: number;
    } | null>(null);
    const [previousEmployeeCount, setPreviousEmployeeCount] = useState<number>(0);

    const saveScrollPosition = () => {
        if (grid?.selection) {
            const selection = grid.selection;
            
            const position = {
                row: selection.row,
                col: selection.col,
                row2: selection.row2,
                col2: selection.col2
            };
            
            setSavedSelection(position);
            localStorage.setItem('table-scroll-position', JSON.stringify(position));
        }
    };

    const restoreScrollPosition = () => {
        if (!grid || isUpdating) {
            return;
        }

        let positionToRestore = savedSelection;
        
        if (!positionToRestore) {
            const savedPosition = localStorage.getItem('table-scroll-position');
            if (savedPosition) {
                try {
                    positionToRestore = JSON.parse(savedPosition);
                } catch (error) {
                    return;
                }
            }
        }

        if (positionToRestore && positionToRestore.row >= 0) {
            try {
                grid.deferUpdate(() => {
                    grid.select(
                        positionToRestore?.row,
                        positionToRestore?.col,
                        positionToRestore?.row2,
                        positionToRestore?.col2
                    );
                    
                    grid.scrollIntoView(positionToRestore?.row, positionToRestore?.col || 0);
                });
                
                setSavedSelection(null);
                
            } catch (error) {
                console.warn('Error al restaurar selección desde Table:', error);
            }
        }
    };

    const updateEmployeesData = (newEmployees: NodeModel[]) => {
        if (!grid) {
            return;
        }

        setIsUpdating(true);
        
        saveScrollPosition();
        
        grid.deferUpdate(() => {
            setEmployeesToShow(newEmployees);
        });
        
        setTimeout(() => {
            setIsUpdating(false);
            restoreScrollPosition();
        }, 50);
    };

    const {
        interruptionTypesList,
        interruptionList,
        workshiftShedulingList,
        inconsistenciesValidation,
        langResources,
        holidaysList,
        workshiftsSchedulingsCopied,
        allTimeAttendanceEvents,
    } = useContext(AppContext);

    const getInfoTooltipExceeded = (
        fechaStr: string,
        employeeId: string,
        dataExceededList: ExceededWeeklyScheduleModel[],
        status: string | number | undefined
    ) => {
        let info = '';

        if (status === 'true' || status == '1') {
            if (dataExceededList && dataExceededList.length > 0) {
                dataExceededList.map((e) => {
                    if (
                        isDateInRangeDate(
                            e.startDate.split('T')[0],
                            e.endDate.split('T')[0],
                            fechaStr
                        )
                    ) {
                        return (info = `
${
    e.hoursDifference > 0
        ? langResources['total.hours.allowed.title']
        : langResources['total.hours.allowed.title2']
}
${langResources['allowed.hours']}: ${e.baseTotalHourLiq}
${langResources['total.hours.scheduled']}: ${e.totalWeeklyScheduledHours}
${langResources['total.hours.difference']}: ${e.hoursDifference}
                    `);
                    }
                });
            }
        }

        return info;
    };

    function isDateInRangeDate(
        initialDate: string,
        endDate: string,
        date: string
    ): boolean {
        const startDate = new Date(initialDate);
        const finalDate = new Date(endDate);
        const cDate = new Date(date);

        return cDate >= startDate && cDate <= finalDate;
    }

    /**
     * Obtenemos el nombre del grupo mediante el id del registro
     * @param parentId string | number
     * @returns  string
     */
    const getParentGroup = (parentId: string | number) => {
        let text = groupsList.filter((employee) => employee?.id === parentId)[0]
            ?.text;

        return text;
    };

    /**
     * Obtenemos el total de horas de las interrupciones por empleado (Si tiene interrupciones asignadas)
     * @param employeeID string | number
     * @returns string | undefined
     */
    const getTotalHoursInterruptions = (employeeID: string | number) => {
        return interruptionList.totalsHour
            ?.find((i) => i.employeeId == employeeID)
            ?.totalHours.toFixed(2);
    };

    /**
     * Obtenemos el total de horas de los turnos por empleado (si tiene turnos asignados)
     * @param employeeID string | number
     * @returns string | undefined
     */
    const getTotalHoursWorkshifts = (employeeID: string | number) => {
        return workshiftShedulingList.totalizedHours
            ?.find((i) => i.employeeId == employeeID)
            ?.totalHours.toFixed(2);
    };

    /**
     * Si tiene turno obtenemos el nombre de la clase de lo contrario no
     * @param date string
     * @param date2 string | undefined
     * @returns "assignedTurn" | undefined
     */
    const getWorkshiftClass = (date: string, date2: string | undefined) => {
        if (formatDate(date) === formatDate(date2)?.split('T')[0]) {
            return 'assignedTurn';
        }
        return undefined;
    };

    /**
     * Obtenemos el nombre de la clase si este contiene interrupciones
     * @param interruption InterruptionModel[][]
     * @returns "bgInterruption" | undefined
     */
    const getInterruptionClass = (interruption: InterruptionModel[]) => {
        if (interruption.length > 0) {
            return 'bgInterruption';
        }
        return undefined;
    };

    /**
     * Se crea todo el tooltip relacionado a los turnos asignados
     * @param date string | number
     * @param id string | number
     * @returns string
     */
    const getInfoToolTipWorkshift = (date: string | number, id: string | number) => {
        let workshift = getWorkshiftScheduling(date, id, workshiftShedulingList);

        if (workshift) {
            let additionalTimeList = workshift.additionalTimes;

            let additionalTimeMessage = '';

            if (additionalTimeList.length > 0) {
                if (additionalTimeList.length > 1) {
                    additionalTimeMessage =
                        `\nEl empleado tiene para esta fecha ${additionalTimeList.length} Tiempos Adicionales programados ` +
                        '\n';
                } else {
                    let additionalTime = additionalTimeList[0];
                    additionalTimeMessage =
                        '\n' +
                        langResources['tooltip.interruption']['additional.title'] +
                        '\n' +
                        langResources['create.interruption.Modal']['starDate'] +
                        ':' +
                        showDateInfo(additionalTime.startTimeAdditionalTime).split(
                            ','
                        )[1] +
                        ' - ' +
                        langResources['create.interruption.Modal']['endDate'] +
                        ':' +
                        showDateInfo(additionalTime.endTimeAdditionalTime).split(
                            ','
                        )[1] +
                        '\n' +
                        langResources['created.by.label'] +
                        ': ' +
                        additionalTime.userName +
                        '\n' +
                        langResources['created.date.label'] +
                        ': ' +
                        dateTimeFormat(additionalTime.createdDate) +
                        '\n';
                }
            }

            let infoWorkshift =
                langResources['tooltip.workshift']['name'].toUpperCase() +
                '\n' +
                workshift.workshift?.alias +
                ' - ' +
                workshift.workshift?.name;

            let infoDates = !workshift.workshift?.isFloating
                ? '\n' +
                  langResources['create.workshift.Modal']['entry'] +
                  ':' +
                  showDateInfo(workshift.startTime).split(',')[1] +
                  ' - ' +
                  langResources['create.workshift.Modal']['exit'] +
                  ':' +
                  showDateInfo(workshift.endTime).split(',')[1]
                : '';

            return workshift.workshift?.id
                ? `${infoWorkshift + infoDates} \n${
                      workshift.isExceptional
                          ? langResources['tooltip.workshift']['exceptional'] + ' \n'
                          : ''
                  }${langResources['tooltip.workshift']['created.by']}: ${
                      workshift.userName ?? ''
                  } \n${
                      langResources['tooltip.workshift']['created.date']
                  }: ${dateTimeFormat(workshift.createdDate)}\n` +
                      additionalTimeMessage
                : additionalTimeMessage;
        } else {
            return '';
        }
    };

    const getInfoOperationCenter = (date: string | number, id: string | number) => {
        let existOperationCenter = workshiftShedulingList.schedulings.find((op) => {
            if (
                op.workshiftSchedulingOperationCenter &&
                op.employeeId == id &&
                op.workshiftSchedulingOperationCenter.startDate.split('T')[0] ===
                date
            ) {
                return op;
            }
        });

        if (existOperationCenter) {
            return `\nCENTRO DE OPERACIONES:\n${existOperationCenter.workshiftSchedulingOperationCenter?.operationCenterName}`;
        }
        return '';
    };

    /**
     * Se crea el tooltip relacionado con todo el tema de las interrupciones
     * @param date string | number
     * @param id string | number
     * @returns string | false
     */
    const getInfoToolTipInterruption = (
        date: string | number,
        id: string | number
    ) => {
        if (getInterruptionListByDate(date, id, interruptionList).length > 0) {
            let result = interruptionTypesList.find(
                (interr) =>
                    interr.id ==
                    getInterruptionListByDate(date, id, interruptionList)[0]
                        .interruptionTypeId
            );

            let interr = getInterruptionListByDate(date, id, interruptionList)[0];

            let isInterruption = `\n${
                interr.interruptionTypeId == 28
                    ? langResources['tooltip.interruption']['additional.title']
                    : langResources['tooltip.interruption']['interruption.title'] +
                      '\n' +
                      result?.name.toLowerCase()
            } `;

            return getInterruptionListByDate(date, id, interruptionList).length > 1
                ? '\n' +
                      langResources['tooltip.interruption']['total.interruptions'] +
                      ' ' +
                      getInterruptionListByDate(date, id, interruptionList).length +
                      ' ' +
                      langResources['tooltip.interruption'][
                          'total.interruptions.assigned'
                      ] +
                      '\n'
                : getInterruptionListByDate(date, id, interruptionList).length ==
                      1 &&
                      isInterruption +
                          '\n' +
                          langResources['create.interruption.Modal']['starDate'] +
                          ':' +
                          showDateInfo(interr.startDate).split(',')[1] +
                          ' - ' +
                          langResources['create.interruption.Modal']['endDate'] +
                          ':' +
                          showDateInfo(interr.endDate).split(',')[1] +
                          '\n' +
                          langResources['created.by.label'] +
                          ': ' +
                          interr.createdBy +
                          '\n' +
                          langResources['created.date.label'] +
                          ': ' +
                          dateTimeFormat(interr.createdDate) +
                          '\n';
        } else {
            return '';
        }
    };

    /**
     * Se crea el tooltip relacionado con los centros de costo
     * @param workshift WorkshiftSchedulingModel | undefined
     * @param interruption InterruptionModel | undefined
     * @returns string
     */
    const getInfoToolTipCostCenter = (
        date: string,
        employeeId: number | string | undefined
    ) => {
        const dateStr = date.split('T')[0];

        const costCenters = workshiftShedulingList.schedulings
            .filter((e) => {
                if (!Array.isArray(e.costCenters) || e.employeeId != employeeId) {
                    return false;
                }

                return e.costCenters.some((cc: any) => {
                    const ccDisplayDate = cc.displayDate?.split('T')[0] || cc.startDate.split('T')[0];
                    return ccDisplayDate === dateStr;
                });
            })
            .flatMap((e) =>
                e.costCenters
                    ? e.costCenters.filter((cc: any) => {
                        const ccDisplayDate = cc.displayDate?.split('T')[0] || cc.startDate.split('T')[0];
                        return ccDisplayDate === dateStr;
                    })
                    : []
            );

        if (costCenters.length > 0) {
            return (
                '\n' +
                langResources['tooltip.cost.center']['name'] +
                (costCenters.length > 1 ? ` (${costCenters.length} asignaciones)` : '') +
                '\n---\n' +
                costCenters
                    .map(
                        (cc: any, index: number) =>
                            `${index + 1}. ${cc.costCenterName} (${cc.costCenterId})\n` +
                            `   ${cc.startDate?.split('T')[1]?.slice(0, 5) || '00:00'} - ${cc.endDate?.split('T')[1]?.slice(0, 5) || '23:59'}\n` +
                            `   ${langResources['created.by.label']}: ${cc.userName || ''}\n` +
                            `   ${langResources['created.date.label']}: ${dateTimeFormat(cc.createdDate)}`
                    )
                    .join('\n---\n')
            );
        } else {
            return (
                '\n' + langResources['tooltip.cost.center']['without.cost.center']
            );
        }
    };

    /**
     * Se crea el tooltip realcionado con las unidades de negocio
     * @param workshift WorkshiftSchedulingModel | undefined
     * @param interruption InterruptionModel | undefined
     * @returns string
     */
    const getInfoToolTipBusinessUnits = (
        workshift: WorkshiftSchedulingModel | undefined,
        interruption: InterruptionModel | undefined
    ) => {
        if (
            workshift?.businessUnit !== null &&
            workshift?.businessUnit.businessUnitName !== undefined
        ) {
            return (
                `\n${
                    langResources['tooltip.bussinnes.unit']['name']
                } \n${workshift?.businessUnit?.businessUnitName.toLowerCase()}` +
                '\n' +
                langResources['created.by.label'] +
                ': ' +
                workshift.businessUnit.userName +
                '\n' +
                langResources['created.date.label'] +
                ': ' +
                dateTimeFormat(workshift.businessUnit.createdDate) +
                '\n'
            );
        } else if (
            interruption?.businessUnit !== null &&
            interruption?.businessUnit.businessUnitName !== undefined
        ) {
            return (
                `\n${
                    langResources['tooltip.bussinnes.unit']['name']
                } \n${interruption?.businessUnit?.businessUnitName.toLowerCase()}` +
                '\n' +
                langResources['created.by.label'] +
                ': ' +
                interruption.businessUnit.userName +
                '\n' +
                langResources['created.date.label'] +
                ': ' +
                dateTimeFormat(interruption.businessUnit.createdDate) +
                '\n'
            );
        } else {
            if (workshift !== undefined || interruption !== null) {
                return (
                    '\n' +
                    langResources['tooltip.bussinnes.unit']['without.bussinnes.unit']
                );
            } else {
                return '';
            }
        }
    };

    /**
     * Esta funcion permite todo el tema de seleccion multiple con el ctrl + click
     * @param e MouseEvent
     * @param employee NodeModel
     * @param date string
     */
    const handlerMultipleSelection = (
        e: MouseEvent,
        employee: NodeModel,
        date: string
    ) => {
        let recoveredData = window.localStorage.getItem('selection-range');

        if (e.ctrlKey || e.metaKey) {
            if (recoveredData) {
                let data: Array<{
                    employeeId: string | number;
                    text: string;
                    date: string;
                    parent: string | number;
                }> = JSON.parse(recoveredData);

                let newEmployee = {
                    employeeId: employee?.id,
                    date: date,
                    text: employee.text,
                    parent: employee.parent,
                };

                const findEquals = data.filter(
                    (emp) =>
                        emp.date === newEmployee.date &&
                        emp.employeeId === newEmployee.employeeId
                );

                if (findEquals.length > 0) {
                    const filterData = data.filter((emp) => emp !== findEquals[0]);

                    localStorage.setItem(
                        'selection-range',
                        JSON.stringify(filterData)
                    );
                } else {
                    data.push(newEmployee);
                    localStorage.setItem('selection-range', JSON.stringify(data));
                }
            } else {
                localStorage.setItem(
                    'selection-range',
                    JSON.stringify([
                        {
                            employeeId: employee?.id,
                            date: date,
                            text: employee.text,
                            parent: employee.parent,
                        },
                    ])
                );
            }
        } else {
            window.localStorage.removeItem('selection-range');
        }
    };

    /**
     * Inicializamos la taba o grilla
     * @param flexGrid
     */
    const initializeGrid = (flexGrid: any) => {
        setGrid(flexGrid);
        // @ts-ignore
        grid?.onSelectionChanged(null);
    };

    useEffect(() => {
        if (workshiftShedulingList) {
            let employeesToShow = workshiftShedulingList?.totalizedHours?.map(
                (e) => e.employeeId
            );

            let a = getChildNodes(treeEmployees).filter((e) => {
                //@ts-ignore
                return employeesToShow?.includes(Number(e?.id));
            });

            if (a.length > 0) {
                if (grid) {
                    updateEmployeesData(a);
                } else {
                    setEmployeesToShow(a);
                }
            }
        }
    }, [workshiftShedulingList]);

    useEffect(() => {
        if (grid && employeesToShow.length > 0 && !isUpdating) {
            if (savedSelection) {
                setTimeout(() => {
                    restoreScrollPosition();
                }, 30);
            }
        }
    }, [grid, employeesToShow, isUpdating]);

    useEffect(() => {
        if (previousEmployeeCount > 0 && employeesToShow.length !== previousEmployeeCount) {
            setSavedSelection(null);
            localStorage.removeItem('table-scroll-position');
        }
        
        setPreviousEmployeeCount(employeesToShow.length);
    }, [employeesToShow.length]);

    const handleGridRefreshed = () => {
        if (savedSelection) {
            setTimeout(() => {
                restoreScrollPosition();
            }, 10);
        }
    };

    return (
        <div id='myContextMenu'>
            {/* Componente de la grilla, con todas sus propiedades  */}
            <Wj.FlexGrid
                frozenColumns={2}
                stickyHeaders={true}
                alternatingRowStep={0}
                refreshed={handleGridRefreshed}
                showMarquee={true}
                anchorCursor={true}
                allowPinning={false}
                selectionMode='MultiRange'
                showSelectedHeaders='All'
                itemsSource={employeesToShow}
                initialized={(e: any) => initializeGrid(e)}
                itemFormatter={(panel: any, r: any, c: any, cell: any) => {
                    //Aca validamos que las fechas festivas se pinten de un color diferente el cellTpe 2 indica la fila inicial superior
                    // La cual es la de las fechas
                    // r => row
                    // c => column
                    if (panel.cellType == '2' && c >= 1) {
                        let date = getDatesBetweenDates(startDate, endDate)[c - 2];

                        let isHoliday = getHolidayDay(date, holidaysList);

                        if (getDayOfWeek(date) == 'Dom' || isHoliday !== undefined) {
                            cell.classList.add('holiday');
                            cell.setAttribute(
                                'title',
                                `${isHoliday?.name ? isHoliday?.name : 'Feriado'}`
                            );
                        }
                        cell.classList.add('fs-9', 'fw-light');
                    }

                    // Aca hablamos de las
                    if (panel.cellType == '1') {
                        //Estamos setteando el nombre, cedula y grupo del empleado en la primera columna
                        if (c < 1) {
                            let employee = getChildNodes(employeesToShow)[r];

                            if (employee) {
                                let containerEmployeeName =
                                    document.createElement('div');
                                containerEmployeeName.setAttribute(
                                    'title',
                                    employee?.text +
                                        '\n' +
                                        getParentGroup(employee?.parent)
                                );
                                containerEmployeeName.setAttribute(
                                    'style',
                                    'font-weight: 500'
                                );
                                containerEmployeeName.innerHTML = `
                            <p class='text-dark w-100 m-0 p-0' style="font-size:10.5px; background: #fff" >
                                ${
                                    employee?.text.split('(').at(0)!.length > 27
                                        ? employee.text
                                              .split('(')
                                              .at(0)
                                              ?.substring(0, 27) + '...'
                                        : employee.text.split('(').at(0)
                                }
                            </p>
                            <p style="marginTop: -15px; background: #fff; font-size:10px;" class="d-flex justify-content-between align-items-center">
                                <span>${
                                    employee.text
                                        .split('(')
                                        .at(1)
                                        ?.replace(')', ' ') ?? ''
                                } 
                                </span>

                                <span class="text-muted">
                                    ${
                                        getParentGroup(employee.parent)
                                            ? getParentGroup(employee.parent)
                                                  .length > 20
                                                ? getParentGroup(
                                                      employee.parent
                                                  )?.substring(0, 17) + '...'
                                                : getParentGroup(employee.parent)
                                            : ''
                                    }
                                </span>
                                
                                
                            </p>`;
                                cell.appendChild(containerEmployeeName);
                            }
                        }

                        //Columna realcionada al total de la horas
                        if (c == 1 && r >= 0) {
                            let containerTotalHours = document.createElement('div');
                            containerTotalHours.classList.add(
                                'dropdown',
                                'w-100',
                                'h-100'
                            );

                            let employee = getChildNodes(employeesToShow)[r];

                            const btn = document.createElement('span');
                            btn.classList.add(
                                'btn',
                                'btn-sm',
                                'bg-light',
                                'd-flex',
                                'justify-content-center',
                                'align-items-baseline',
                                'text-center',
                                'h-100',
                                'w-100'
                            );

                            const workshift = document.createElement('span');
                            workshift.setAttribute(
                                'title',
                                langResources[
                                    'total.hours.workshift.scheduling.label'
                                ]
                            );
                            const interruption = document.createElement('span');
                            interruption.setAttribute(
                                'title',
                                langResources[
                                    'total.hours.interruption.scheduling.label'
                                ]
                            );

                            if (getTotalHoursWorkshifts(employee?.id)) {
                                btn.classList.add('assignedTurn');
                                workshift.innerHTML = `${getTotalHoursWorkshifts(
                                    employee?.id
                                )}`;
                            }

                            if (getTotalHoursInterruptions(employee?.id)) {
                                btn.classList.add('assignedTurn');
                                interruption.classList.add('assignedInterruption');
                                interruption.innerHTML = `${getTotalHoursInterruptions(
                                    employee?.id
                                )}`;
                            }

                            btn.appendChild(workshift);
                            btn.appendChild(interruption);
                            containerTotalHours.appendChild(btn);
                            cell.appendChild(containerTotalHours);
                        }

                        //Celdas relacionadas a las programaciones
                        if (c > 1 && r >= 0) {
                            let notificationsScheduling = 2;

                            const container = document.createElement('div');
                            container.classList.add('dropdown', 'w-100', 'h-100');

                            const btn = document.createElement('span');

                            let employee = getChildNodes(employeesToShow)[r];
                            let date = getDatesBetweenDates(startDate, endDate)[
                                c - 2
                            ];

                            // Validación para los rangos que exceden la programación
                            const parameter = parametersApplication.find(
                                (parameter) =>
                                    parameter.name ===
                                    'WorkshiftSchedulingExceedsEstimatedTime'
                            );

                            let dataExceededList =
                                workshiftShedulingList?.weeksThatExceedSchedule?.filter(
                                    (we) => we.employeeId == employee?.id
                                );

                            if (
                                parameter?.value === 'true' ||
                                parameter?.value == '1'
                            ) {
                                if (dataExceededList) {
                                    dataExceededList.map((e) => {
                                        if (
                                            isDateInRangeDate(
                                                e.startDate.split('T')[0],
                                                e.endDate.split('T')[0],
                                                date
                                            )
                                        ) {
                                            if (e.hoursDifference > 0)
                                                cell.classList.add('exceeded');
                                        }
                                    });
                                }
                            }

                            btn.classList.add(
                                'position-relative',
                                'btn',
                                'btn-sm',
                                'bg-light',
                                'd-flex',
                                'justify-content-center',
                                'align-items-baseline',
                                'text-center'
                            );

                            btn.addEventListener('click', (e) => {
                                handlerMultipleSelection(e, employee, date);
                            });

                            if (
                                workshiftsSchedulingsCopied.workshiftsCopied.length >
                                0
                            ) {
                                let workshiftCopied =
                                    workshiftsSchedulingsCopied.workshiftsCopied.find(
                                        (w) =>
                                            w.employeeId == employee?.id &&
                                            w.date.split('T')[0] ==
                                                date.split('T')[0]
                                    );

                                if (workshiftCopied) {
                                    cell.classList.add('copied__box');
                                }
                            }

                            btn.setAttribute('data-id', `col-${c}/row-${r}`);

                            let classWorkshift = getWorkshiftScheduling(
                                date,
                                employee?.id,
                                workshiftShedulingList
                            )?.workshiftId
                                ? getWorkshiftClass(
                                      date,
                                      getWorkshiftScheduling(
                                          date,
                                          employee?.id,
                                          workshiftShedulingList
                                      )?.date
                                  )
                                : undefined;

                            let classAdditonalTime = getWorkshiftScheduling(
                                date,
                                employee?.id,
                                workshiftShedulingList
                            )?.additionalTimes
                                ? 'assignedAdditionalTime'
                                : undefined;

                            let classInterruption = getInterruptionClass(
                                getInterruptionListByDate(
                                    date,
                                    employee?.id,
                                    interruptionList
                                )
                            );

                            if (classWorkshift) {
                                btn.classList.add(classWorkshift);
                            }

                            if (classAdditonalTime) {
                                btn.classList.add(classAdditonalTime);
                            }

                            if (
                                getInterruptionListByDate(
                                    date,
                                    employee?.id,
                                    interruptionList
                                )[0]?.isAdditionalHour
                            ) {
                                btn.classList.add('assignedAdditionalTime');
                            }

                            if (classInterruption) {
                                btn.classList.add(classInterruption);
                            }

                            // @ts-ignore
                            btn.style = `height: 100%; width:100%;  font-size:11px; ${
                                getWorkshiftScheduling(
                                    date,
                                    employee?.id,
                                    workshiftShedulingList
                                )?.isExceptional
                                    ? 'border: 0.5px solid #FF3A1A'
                                    : ''
                            }`;

                            btn.setAttribute(
                                'title',
                                `${getInfoToolTipWorkshift(
                                    date,
                                    employee?.id
                                )}${getInfoToolTipInterruption(
                                    date,
                                    employee?.id
                                )}${getInfoToolTipCostCenter(
                                    date,
                                    employee?.id
                                )}${getInfoToolTipBusinessUnits(
                                    getWorkshiftScheduling(
                                        date,
                                        employee?.id,
                                        workshiftShedulingList
                                    ),
                                    getFirstInterruption(
                                        getInterruptionListByDate(
                                            date,
                                            employee?.id,
                                            interruptionList
                                        ),
                                        date
                                    )
                                )} ${
                                    allTimeAttendanceEvents.filter(
                                        (e) =>
                                            e.employeeId == employee?.id &&
                                            e.eventDate.split('T')[0] == date
                                    ).length > 0
                                        ? '\nMARCACIÓN MANUAL'
                                        : ''
                                }
                                ${getInfoTooltipExceeded(
                                    date,
                                    employee?.id.toString(),
                                    dataExceededList,
                                    parameter?.value
                                )}${getInfoOperationCenter(
                                    date,
                                    employee?.id.toString()
                                )}
                                `
                            );

                            const containerInfo = document.createElement('div');
                            container.classList.add('d-flex', 'flex-column');

                            const workshift = document.createElement('span');

                            let formattedAlias = getWorkshiftScheduling(
                                date,
                                employee?.id,
                                workshiftShedulingList
                            )?.workshift?.alias;

                            workshift.innerHTML = `${
                                formattedAlias && formattedAlias.length > 5
                                    ? formattedAlias.slice(0, 5) + '...'
                                    : formattedAlias
                            }`;

                            if (
                                getWorkshiftScheduling(
                                    date,
                                    employee?.id,
                                    workshiftShedulingList
                                )?.workshiftId
                            ) {
                                btn.classList.add('assignedTurn');
                                workshift.classList.add('mb-3', 'assignedTurn');
                            } else if (
                                getWorkshiftScheduling(
                                    date,
                                    employee?.id,
                                    workshiftShedulingList
                                )?.workshift?.isFloating
                            ) {
                                btn.classList.add('assignedFloat');
                                workshift.classList.add('mb-3', 'assignedFloat');
                            }

                            const interruption = document.createElement('span');

                            if (
                                getInterruptionListByDate(
                                    date,
                                    employee?.id,
                                    interruptionList
                                )[0]
                            ) {
                                interruption.classList.add('assignedInterruption');
                                interruption.innerHTML = `I${
                                    getInterruptionListByDate(
                                        date,
                                        employee?.id,
                                        interruptionList
                                    )[0]?.nickName
                                }`;
                            }

                            const additionalTime = document.createElement('span');

                            if (
                                getWorkshiftScheduling(
                                    date,
                                    employee?.id,
                                    workshiftShedulingList
                                )?.additionalTimes[0] &&
                                !getWorkshiftScheduling(
                                    date,
                                    employee?.id,
                                    workshiftShedulingList
                                )?.workshiftId
                            ) {
                                additionalTime.innerHTML = `HOAD`;
                                additionalTime.classList.add(
                                    'mb-3',
                                    'assignedAdditionalTime'
                                );
                            } else if (
                                getWorkshiftScheduling(
                                    date,
                                    employee?.id,
                                    workshiftShedulingList
                                )?.additionalTimes[0] &&
                                !getInterruptionListByDate(
                                    date,
                                    employee?.id,
                                    interruptionList
                                )[0]
                            ) {
                                interruption.classList.add('assignedAdditionalTime');
                                interruption.innerHTML = `HOAD`;
                            }

                            let isWorkshiftExceptional = getWorkshiftScheduling(
                                date,
                                employee?.id,
                                workshiftShedulingList
                            )?.isExceptional;

                            if (isWorkshiftExceptional) {
                                notificationsScheduling += 1;
                            }

                            let existAdditionalHourInWorkshift =
                                getWorkshiftScheduling(
                                    date,
                                    employee?.id,
                                    workshiftShedulingList
                                )?.additionalTimes;

                            if (
                                existAdditionalHourInWorkshift &&
                                existAdditionalHourInWorkshift.length > 0
                            ) {
                                notificationsScheduling += 1;
                            }

                            let existTimeAttendance = allTimeAttendanceEvents.filter(
                                (e) =>
                                    e.employeeId == employee?.id &&
                                    e.eventDate.split('T')[0] == date
                            );

                            if (
                                existTimeAttendance &&
                                existTimeAttendance.length > 0
                            ) {
                                notificationsScheduling += 1;
                            }

                            let existOperationCenter =
                                workshiftShedulingList.schedulings.find((op) => {
                                    if (
                                        op.workshiftSchedulingOperationCenter &&
                                        op.workshiftSchedulingOperationCenter.startDate.split(
                                            'T'
                                        )[0] === date
                                    ) {
                                        return op;
                                    }
                                });

                            if (existOperationCenter) {
                                notificationsScheduling += 1;
                            }

                            let existCostCenterInWorkshifts = getWorkshiftScheduling(
                                date,
                                employee?.id,
                                workshiftShedulingList
                            )?.costCenters;

                            let existCostCenterInInterruptions =
                                getFirstInterruption(
                                    getInterruptionListByDate(
                                        date,
                                        employee?.id,
                                        interruptionList
                                    ),
                                    date
                                )?.costCenters;

                            if (
                                existCostCenterInWorkshifts ||
                                existCostCenterInInterruptions
                            ) {
                                notificationsScheduling -= 1;
                            }

                            let existBusinessUnitInWorkshifts =
                                getWorkshiftScheduling(
                                    date,
                                    employee?.id,
                                    workshiftShedulingList
                                )?.businessUnit;

                            let existBusinessUnitInterruptions =
                                getFirstInterruption(
                                    getInterruptionListByDate(
                                        date,
                                        employee?.id,
                                        interruptionList
                                    ),
                                    date
                                )?.businessUnit;

                            if (
                                existBusinessUnitInWorkshifts ||
                                existBusinessUnitInterruptions
                            ) {
                                notificationsScheduling -= 1;
                            }

                            const notifications = document.createElement('span');
                            notifications.classList.add(
                                'position-absolute',
                                'top-0',
                                'start-100',
                                'translate-middle',
                                'badge',
                                'rounded-pill',
                                'bg-info'
                            );

                            const notificationsAvailability =
                                document.createElement('span');
                            notificationsAvailability.classList.add(
                                'position-absolute',
                                'top-50',
                                'start-0',
                                'translate-middle',
                                'badge',
                                'rounded-pill',
                                'bg-warning',
                                'bagde-left'
                            );

                            notificationsAvailability.innerHTML = 'D';

                            if (notificationsScheduling > 0) {
                                notifications.innerHTML = `
                                ${notificationsScheduling}<span class='visually-hidden'>unread messages</span>
                                `;
                            }

                            const notificationsInconsistencies =
                                document.createElement('span');

                            let hasEmployeeInconsistencies: InconsistenciesByEmployeeModel[] =
                                [];
                            let hasEmployeeInconsistenciesInThisDate:
                                | InconsistenciesByEmployeeModel
                                | undefined = {} as InconsistenciesByEmployeeModel;

                            if (
                                inconsistenciesValidation.inconsistenciesByEmployee
                            ) {
                                notificationsInconsistencies.classList.add(
                                    'position-absolute',
                                    'translate-middle',
                                    'badge',
                                    'rounded-pill',
                                    'bg-danger',
                                    'bagde-left'
                                );

                                hasEmployeeInconsistencies =
                                    inconsistenciesValidation.inconsistenciesByEmployee.filter(
                                        (e) => e.employeeId == employee?.id
                                    );

                                hasEmployeeInconsistenciesInThisDate =
                                    hasEmployeeInconsistencies.find(
                                        (e) => e.date.split('T')[0] == date
                                    );

                                if (hasEmployeeInconsistenciesInThisDate) {
                                    notificationsInconsistencies.innerHTML = `
                                ${hasEmployeeInconsistenciesInThisDate.inconsistencies.length}<span class='visually-hidden'>unread messages</span>
                                `;
                                }
                            }

                            if (
                                (getWorkshiftClass(
                                    date,
                                    getWorkshiftScheduling(
                                        date,
                                        employee?.id,
                                        workshiftShedulingList
                                    )?.date
                                ) !== undefined &&
                                    getWorkshiftScheduling(
                                        date,
                                        employee?.id,
                                        workshiftShedulingList
                                    )?.workshiftId) ||
                                getWorkshiftScheduling(
                                    date,
                                    employee?.id,
                                    workshiftShedulingList
                                )?.workshift?.isFloating
                            ) {
                                containerInfo.appendChild(workshift);
                            }

                            if (
                                !getWorkshiftScheduling(
                                    date,
                                    employee?.id,
                                    workshiftShedulingList
                                )?.workshiftId &&
                                getWorkshiftScheduling(
                                    date,
                                    employee?.id,
                                    workshiftShedulingList
                                )?.additionalTimes
                            ) {
                                containerInfo.appendChild(additionalTime);
                            }

                            if (
                                getInterruptionListByDate(
                                    date,
                                    employee?.id,
                                    interruptionList
                                ).length > 0 ||
                                getWorkshiftScheduling(
                                    date,
                                    employee?.id,
                                    workshiftShedulingList
                                )?.additionalTimes[0]
                            ) {
                                containerInfo.appendChild(interruption);
                            }

                            let notHasInconsistencies =
                                hasEmployeeInconsistenciesInThisDate &&
                                hasEmployeeInconsistenciesInThisDate.inconsistencies &&
                                hasEmployeeInconsistenciesInThisDate.inconsistencies
                                    .length > 0;

                            if (
                                !notHasInconsistencies &&
                                (getWorkshiftClass(
                                    date,
                                    getWorkshiftScheduling(
                                        date,
                                        employee?.id,
                                        workshiftShedulingList
                                    )?.date
                                ) !== undefined ||
                                    getInterruptionListByDate(
                                        date,
                                        employee?.id,
                                        interruptionList
                                    ).length !== 0) &&
                                !getWorkshiftScheduling(
                                    date,
                                    employee?.id,
                                    workshiftShedulingList
                                )?.workshift?.isFloating
                            ) {
                                btn.appendChild(notifications);
                            }

                            if (
                                hasEmployeeInconsistenciesInThisDate &&
                                hasEmployeeInconsistenciesInThisDate.inconsistencies &&
                                hasEmployeeInconsistenciesInThisDate.inconsistencies
                                    .length > 0
                            ) {
                                btn.appendChild(notificationsInconsistencies);
                            }

                            if (
                                getWorkshiftScheduling(
                                    date,
                                    employee?.id,
                                    workshiftShedulingList
                                )?.hasAvailability
                            ) {
                                btn.appendChild(notificationsAvailability);
                            }

                            btn.appendChild(containerInfo);
                            container.appendChild(btn);
                            cell.appendChild(container);
                        }
                    }
                }}
            >
                {/* Columna del nombre */}
                <Wj.FlexGridColumn
                    binding=''
                    header='    '
                    isReadOnly={true}
                    width={200}
                    allowResizing={false}
                    allowDragging={false}
                    allowSorting={false}
                    cssClass='text-start'
                />

                {/* Columna de las horas */}
                <Wj.FlexGridColumn
                    binding=''
                    header={langResources['total.hours.scheduling.label']}
                    isReadOnly={true}
                    width={65}
                    allowResizing={false}
                    allowDragging={false}
                    allowSorting={false}
                />

                {/* Se va a renderizar columna por fecha hasta que se cumpla el rango */}
                {getDatesBetweenDates(startDate, endDate).map((date, index) => (
                    <Wj.FlexGridColumn
                        binding={date}
                        header={`${getMonth(date)}  - ${
                            formatShowDate(date).split('-')[1]
                        }  ${formatShowDate(date).split('-')[0]}`}
                        isReadOnly={true}
                        width={65}
                        allowResizing={false}
                        allowDragging={false}
                        allowSorting={false}
                        key={index}
                        cssClass='d-flex flex-align-center justify-content-center'
                    ></Wj.FlexGridColumn>
                ))}
            </Wj.FlexGrid>
        </div>
    );
};
